import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { PillItem } from "../../components/pill"
import { JobbinPrimaryButton } from "../../components/primaryButton"
import { JobbinSecondaryButton } from "../../components/secondaryButton"
import { useUpdateFavouriteCandidates } from "../../hooks/candidate-hooks"
import { useCredits } from "../../context/credits"
import { CandidateDetails, AvailabilityInterval } from "./model"
import { formatHours } from "../../utils/availability"
import * as candidatesService from "../../../src/services/candidates-service"
import SendGigModal from "../../components/sendGigModal"
import { CandidateStatsCard } from "../../components/candidateStats"

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString("ro-RO", { day: "numeric", month: "short", year: "numeric" })
  } catch {
    return iso
  }
}

const cleanLocation = (loc?: string) => loc?.replace(/\s*\(null\)/gi, "").trim() ?? ""

const PreferredGigPills = ({
  titles,
  className = "",
}: {
  titles?: string[]
  className?: string
}) => {
  if (!titles || titles.length === 0) return null
  const visible = titles.slice(0, 2)
  const rest = titles.slice(2)
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {visible.map((pozitie, index) => (
        <PillItem key={`${pozitie}-${index}`} text={pozitie} classes="" />
      ))}
      {rest.length > 0 && (
        <div className="group relative" title={rest.join(", ")}>
          <div className="bg-figmaBlack rounded-[100px] h-fit px-[12px] py-[8px] cursor-default">
            <p className="text-white text-[14px]">+{rest.length} poziții</p>
          </div>
          <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 hidden -translate-x-1/2 group-hover:block">
            <div className="max-w-[260px] rounded-xl bg-figmaBlack px-3 py-2 shadow-lg">
              <div className="flex flex-wrap gap-1.5">
                {rest.map((pozitie, index) => (
                  <span
                    key={`${pozitie}-${index}`}
                    className="whitespace-nowrap rounded-full bg-white/15 px-2 py-1 text-[12px] text-white"
                  >
                    {pozitie}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export const CandidatePage = () => {
  const { id } = useParams()
  const [candidateData, setCandidateData] = useState<CandidateDetails>()
  const [isSendGigModalOpen, setIsSendGigModalOpen] = useState(false)
  const [isPhotoOpen, setIsPhotoOpen] = useState(false)

  useEffect(() => {
    if (!id) return

    candidatesService.getCandidateById(id).then((candidate: CandidateDetails) => {
      setCandidateData(candidate)
    })
  }, [id])

  const { updateFavouitesCandidates } = useUpdateFavouriteCandidates()
  const { pricing } = useCredits()
  const invitationCost = pricing?.invitationCost ?? 0

  const addToFavorites = () => {
    if (!id || !candidateData) return

    updateFavouitesCandidates(id).then(() => {
      setCandidateData({
        ...candidateData,
        isFavourite: !candidateData.isFavourite,
      })
    })
  }

  const closeSendGigModal = () => {
    setIsSendGigModalOpen(false)
  }

  const openSendGigModal = () => {
    setIsSendGigModalOpen(true)
  }

  return (
      <div id="candidate-page" className="w-full px-4 py-4 sm:px-6 sm:py-6">
        <SendGigModal
            isOpen={isSendGigModalOpen}
            closeModal={closeSendGigModal}
            candidateName={candidateData ? `${candidateData.firstName} ${candidateData.lastName}` : undefined}
        />

        {/* Lightbox poza */}
        {isPhotoOpen && candidateData?.profilePictureUrl && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
            onClick={() => setIsPhotoOpen(false)}
          >
            <img
              src={candidateData.profilePictureUrl}
              alt={`${candidateData.firstName} ${candidateData.lastName}`}
              className="max-h-[80vh] max-w-[80vw] rounded-2xl object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Candidate bar */}
        <div
            id="candidate-bar"
            className="bg-white rounded-[16px] p-4 sm:p-6 flex flex-col items-center text-center xl:flex-row xl:items-center xl:justify-between xl:text-left"
            style={{ border: "1px solid #e5e7eb" }}
        >
          {/* Stanga: poza + nume + ocupatie + rating */}
          <div className="flex flex-col items-center text-center xl:flex-row xl:items-center xl:gap-6 xl:text-left">
            {/* Poza */}
            <div
              className={`flex h-[110px] w-[110px] mx-auto xl:mx-0 shrink-0 rounded-full bg-figmaLightBg ${candidateData?.profilePictureUrl ? "cursor-zoom-in" : ""}`}
              onClick={() => candidateData?.profilePictureUrl && setIsPhotoOpen(true)}
            >
              <div className="m-auto flex h-[90px] w-[90px] items-center justify-center rounded-full bg-figmaSpecialBlue bg-opacity-25">
                {candidateData?.profilePictureUrl ? (
                    <img
                        src={candidateData.profilePictureUrl}
                        className="h-[90px] w-[90px] rounded-full object-cover"
                        alt={`${candidateData.firstName} ${candidateData.lastName}`}
                    />
                ) : (
                    <p className="text-lg font-semibold text-figmaBlack">
                      {candidateData?.firstName?.[0]}
                      {candidateData?.lastName?.[0]}
                    </p>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="mt-3 xl:mt-0 min-w-0">
              <p className="text-[22px] sm:text-[26px] lg:text-[28px] font-semibold text-figmaBlack break-words">
                {candidateData ? `${candidateData.firstName} ${candidateData.lastName}` : ""}
              </p>

              {/* Mobile: ocupatie + oras, rating separat */}
              <div className="mt-1 flex flex-wrap items-center justify-center xl:hidden gap-2">
                <p className="text-figmaDarkGray text-[14px] break-words">
                  {candidateData?.preferredGigTitles?.[0] ?? ""}
                </p>
                {candidateData?.location && (
                    <>
                      <span className="text-figmaDarkGray opacity-40">•</span>
                      <div className="flex items-center gap-1">
                        <img src="/images/navigation.svg" alt="location" className="h-[14px] shrink-0" />
                        <p className="text-figmaDarkGray text-[14px] break-words">{cleanLocation(candidateData.location)}</p>
                      </div>
                    </>
                )}
              </div>
              <div className="mt-2 flex items-center justify-center gap-1 xl:hidden">
                <img src="/images/rate.svg" className="h-fit shrink-0" alt="rating" />
                <p className="text-figmaContrast font-semibold">{candidateData?.rating || ""}</p>
              </div>

              {/* Desktop: ocupatie + nota pe acelasi rand */}
              <div className="hidden xl:flex items-center gap-3 mt-1">
                <p className="text-figmaDarkGray text-[14px]">
                  {candidateData?.preferredGigTitles?.[0] ?? ""}
                </p>
                <span className="text-figmaDarkGray opacity-40">•</span>
                <div className="flex items-center gap-1">
                  <img src="/images/rate.svg" className="h-fit shrink-0" alt="rating" />
                  <p className="text-figmaContrast font-semibold">{candidateData?.rating || ""}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Dreapta: locatie + pozitii (doar desktop) */}
          <div className="hidden xl:flex xl:items-center xl:gap-4">
            <div className="border-x-2 border-gray-100 px-10 text-center">
              <p className="text-xs text-figmaDarkGray">LOCAȚIE</p>
              <p className="text-figmaPrimary break-words">{cleanLocation(candidateData?.location)}</p>
            </div>
            <PreferredGigPills titles={candidateData?.preferredGigTitles} />
          </div>

          {/* Pozitii pe mobil */}
          <PreferredGigPills
              titles={candidateData?.preferredGigTitles}
              className="mt-3 justify-center xl:hidden"
          />
        </div>

        {/* Scoruri candidat */}
        <CandidateStatsCard
          className="mt-6"
          rating={candidateData?.rating}
          responseRate={candidateData?.responseRate}
          responseRateShiftCount={candidateData?.responseRateShiftCount}
          attendance={candidateData?.attendance}
          attendanceShiftCount={candidateData?.attendanceShiftCount}
          numberOfRatings={candidateData?.numberOfRatings}
        />

        {/* Disponibilitate */}
        {candidateData?.availability && candidateData.availability.length > 0 && (
          <div
            className="mt-6 bg-white rounded-2xl overflow-hidden"
            style={{ border: "1px solid #e5e7eb" }}
          >
            <p className="candidate-feature-title px-5 py-4 border-b border-gray-100">Disponibilitate</p>
            <div className="px-5 py-4 flex flex-wrap gap-2">
              {candidateData.availability.map((interval: AvailabilityInterval) => (
                <span
                  key={interval.id}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F2F4F6] text-[13px] font-medium text-figmaBlack"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="shrink-0">
                    <rect x="3" y="4" width="18" height="18" rx="2" stroke="#486af6" strokeWidth="2"/>
                    <path d="M16 2v4M8 2v4M3 10h18" stroke="#486af6" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  {formatDate(interval.dateFrom)} — {formatDate(interval.dateTo)}
                  <span className="text-figmaDarkGray font-normal">
                    · {formatHours(interval.timeFrom, interval.timeTo)}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* About */}
        <div
            id="about"
            className="mt-6 bg-white rounded-2xl overflow-hidden"
            style={{ border: "1px solid #e5e7eb" }}
        >
          <p className="candidate-feature-title px-5 py-4 border-b border-gray-100">Informații despre candidat</p>

          <p className="px-5 py-4 text-[14px] leading-6 text-figmaDarkGray break-words">
            {candidateData?.description || ""}
          </p>

          {/* Serverul trimite cvUrl doar daca avem deja o legatura cu acest candidat
              (a aplicat la un job de-al nostru sau i-am trimis o invitatie). */}
          <div className="px-5 py-4 flex items-center gap-3 border-t border-gray-100">
            <img src="/images/resume-icon.svg" alt="resume" className="shrink-0" />
            {candidateData?.cvUrl ? (
                <a
                    href={candidateData.cvUrl}
                    className="underline text-figmaBlack bold-text text-[14px] break-all"
                    target="_blank"
                    rel="noreferrer"
                >
                  {`${candidateData.firstName} ${candidateData.lastName}`}
                  _cv.pdf
                </a>
            ) : (
                <span className="text-[14px] leading-6 text-figmaDarkGray">
                  CV-ul devine vizibil după ce candidatul aplică la unul dintre joburile
                  tale sau după ce îi trimiți o invitație.
                </span>
            )}
          </div>
        </div>

        {/* Pozitii anterioare + Video + Buttons */}
        <div
            id="pozitii-anterioare-video-buttons"
            className="mt-6 flex flex-col gap-6 xl:flex-row xl:items-start"
        >
          <div
              id="pozitii-anterioare"
              className="w-full bg-white rounded-2xl overflow-hidden"
              style={{ border: "1px solid #e5e7eb" }}
          >
            <p className="candidate-feature-title px-5 py-4 border-b border-gray-100">
              Poziții anterioare
            </p>
            <div className="flex flex-col p-3 max-h-[280px] overflow-y-auto">
              {candidateData?.completedApplications &&
              candidateData.completedApplications.length !== 0 ? (
                  candidateData.completedApplications.map((pozitie, index) => (
                      <div
                          key={index}
                          className="px-3 py-4 break-words"
                          style={{
                            borderBottom:
                                index < candidateData.completedApplications.length - 1
                                    ? "1px solid #f3f4f6"
                                    : "none",
                          }}
                      >
                        <p className="bold-text text-[14px] text-figmaBlack">
                          {pozitie.businessName} - {pozitie.gigTitle}
                        </p>
                        <p className="mt-1 text-[14px] leading-6">
                          <span className="text-figmaPrimary">Recenzie:</span>{" "}
                          <span className="text-figmaDarkGray">{pozitie.description}</span>
                        </p>
                      </div>
                  ))
              ) : (
                  <p className="px-3 py-4 text-[14px] text-figmaDarkGray">Fără poziții anterioare</p>
              )}
            </div>
          </div>

          <div id="video-buttons" className="w-full">
            <div
                className="bg-white rounded-2xl overflow-hidden"
                style={{ border: "1px solid #e5e7eb" }}
            >
              <p className="candidate-feature-title px-5 py-4 border-b border-gray-100">Video</p>
              <div className="flex min-h-[220px] w-full items-center justify-center bg-figmaDark text-center text-white">
                {candidateData?.videoUrl ? (
                    <video
                        id="movie"
                        className="h-auto max-h-[420px] w-full"
                        src={candidateData.videoUrl}
                        controls
                    />
                ) : (
                    <p className="px-4 py-8">Fără video</p>
                )}
              </div>
            </div>

            {/* Butoanele originale pastrate */}
            <div id="buttons" className="mt-6 flex flex-col gap-4 sm:flex-row">
              <div className="w-full">
                <JobbinSecondaryButton
                    onClickAction={addToFavorites}
                    text={candidateData?.isFavourite ? "Șterge de la favoriți" : "Adaugă la favoriți"}
                />
              </div>
              <div className="w-full">
                <JobbinPrimaryButton
                    onClickAction={openSendGigModal}
                    text={invitationCost > 0 ? `Trimite job · ${invitationCost} cr` : "Trimite job"}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
  )
}